import { api } from './client'

/**
 * F-085 / TASK-0339 — scadenziario unico (REQ-176-001, REQ-167-002, REQ-126-001) e scadenze manuali
 * (REQ-177-004). Tutti i totali arrivano dal server; la schermata li mostra. Nessun saldo prospettico,
 * nessun runway, nessun calcolo d'imposta: le imposte sono scadenze inserite a mano.
 */

export const CALENDAR_BUCKETS = [
  'Fornitori', 'Personale', 'Affitti', 'Software', 'Imposte', 'Assicurazioni', 'Provvigioni', 'Finanziamenti', 'Altro',
] as const
export type CalendarBucket = (typeof CALENDAR_BUCKETS)[number]

export const CALENDAR_HORIZONS = [7, 30, 60, 90] as const

export const SCHEDULED_KINDS = ['Imposta', 'Finanziamento', 'Assicurazione', 'Affitto', 'Personale', 'Software', 'Altro'] as const
export const SCHEDULED_FREQUENCIES = ['Mensile', 'Bimestrale', 'Trimestrale', 'Semestrale', 'Annuale', 'Personalizzata'] as const

export type CalendarDirection = 'Entrata' | 'Uscita'

export interface CalendarItem {
  date: string
  bucket: string
  direction: CalendarDirection
  source: { type: string; id: number }
  description: string
  amount: number
  status: string
  overdue: boolean
}

export interface CalendarBucketTotal {
  bucket: string
  entrate: number
  uscite: number
  count: number
}

export interface CalendarWeekTotal {
  weekStart: string
  entrate: number
  uscite: number
  count: number
}

export interface CalendarFlow {
  entrate: number
  uscite: number
  count: number
}

export interface PaymentCalendar {
  today: string
  until: string
  days: number
  buckets: string[]
  items: CalendarItem[]
  totalsByBucket: CalendarBucketTotal[]
  totalsByWeek: CalendarWeekTotal[]
  overdue: CalendarFlow
  next30: CalendarFlow
}

export interface ScheduledPayment {
  id: number
  description: string
  kind: string
  direction: CalendarDirection
  amount: number
  dueDate: string
  frequency?: string | null
  intervalMonths?: number | null
  endDate?: string | null
  status: string
  paidAt?: string | null
  supplierId?: number | null
  expenseCategoryId?: number | null
  counterpartyName?: string | null
  notes?: string | null
  bucket?: string | null
}

export interface ScheduledPaymentBody {
  description: string
  kind: string
  direction: CalendarDirection
  amount: number
  dueDate: string
  frequency?: string | undefined
  intervalMonths?: number | undefined
  endDate?: string | undefined
  supplierId?: number | undefined
  expenseCategoryId?: number | undefined
  counterpartyName?: string | undefined
  notes?: string | undefined
}

export const paymentCalendarApi = {
  get: (params: { days: number; bucket?: string | undefined; direction?: string | undefined }) => {
    const q = new URLSearchParams({ days: String(params.days) })
    if (params.bucket) q.set('bucket', params.bucket)
    if (params.direction) q.set('direction', params.direction)
    return api<PaymentCalendar>(`/api/finance/payment-calendar?${q.toString()}`)
  },
}

export const scheduledPaymentsApi = {
  list: (status?: string) =>
    api<{ kinds: string[]; frequencies: string[]; count: number; items: ScheduledPayment[] }>(
      `/api/finance/scheduled-payments${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  create: (body: ScheduledPaymentBody) =>
    api<{ id: number }>('/api/finance/scheduled-payments', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: ScheduledPaymentBody) =>
    api<void>(`/api/finance/scheduled-payments/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  markPaid: (id: number) =>
    api<ScheduledPayment>(`/api/finance/scheduled-payments/${id}/mark-paid`, { method: 'POST' }),
  cancel: (id: number) => api<void>(`/api/finance/scheduled-payments/${id}`, { method: 'DELETE' }),
}

/** Sceglie il gruppo dello scadenziario di una categoria di spesa (`null` = nessuno). Amministrazione/Admin. */
export const categoryBucketApi = {
  set: (id: number, bucket: string | null) =>
    api<void>(`/api/expense-categories/${id}/calendar-bucket`, { method: 'PUT', body: JSON.stringify({ bucket }) }),
}
